import { AutoTagToolCall } from "@/apis/table/route";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { toolLabel } from "./constants";

type ReferenceRow = {
  score?: number;
  company?: string;
  idlKey?: string;
  sourceKey?: string;
  cellCount?: number;
};
type Binding = { range?: string; tagKey?: string; reason?: string };

const KIND_LABEL: Record<string, string> = {
  llm_response: "최종 응답",
  conversation: "응답",
};

const headerLabel = (call: AutoTagToolCall): string => {
  if (call.kind === "tool_call") return toolLabel(call.tool);
  if (call.kind === "references") {
    const refs = call.response.references as ReferenceRow[] | undefined;
    return `참조할 표 ${refs?.length ?? 0}건`;
  }
  if (call.kind === "warning") {
    return call.response.skipped === true
      ? "축 도메인 없음 — 멤버 건너뜀"
      : "축 도메인 없음 — 다른 축으로 배치";
  }
  return KIND_LABEL[call.kind] ?? call.kind;
};

const JsonBlock = ({ value }: { value: unknown }) => (
  <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-all rounded bg-neutral-50 p-1.5 text-[11px] leading-tight text-neutral-700">
    {JSON.stringify(value, null, 2)}
  </pre>
);

const Body = ({ call }: { call: AutoTagToolCall }) => {
  if (call.kind === "references") {
    const refs = (call.response.references as ReferenceRow[] | undefined) ?? [];
    if (refs.length === 0)
      return <p className="text-[11px] text-neutral-400">참조 없음</p>;
    return (
      <div className="flex flex-col gap-1">
        {refs.map((ref, i) => (
          <div key={i} className="flex items-center gap-1.5 text-[11px]">
            {typeof ref.score === "number" && (
              <span className="shrink-0 rounded bg-neutral-100 px-1 text-neutral-500">
                {ref.score.toFixed(2)}
              </span>
            )}
            <span className="truncate text-neutral-700">
              {ref.company ?? "—"} · {ref.idlKey ?? "—"}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (call.kind === "llm_response") {
    const error = call.response.error as string | undefined;
    if (error) return <p className="text-[11px] text-red-500">{error}</p>;
    const lineitem = (call.response.lineitem as Binding[] | undefined) ?? [];
    const member = (call.response.member as Binding[] | undefined) ?? [];
    const rows = [
      ...lineitem.map((b) => ({ b, type: "라인" })),
      ...member.map((b) => ({ b, type: "멤버" })),
    ];
    if (rows.length === 0)
      return <p className="text-[11px] text-neutral-400">응답 0개</p>;
    return (
      <div className="flex flex-col gap-1">
        {rows.map(({ b, type }, i) => (
          <div key={i} className="text-[11px]">
            <div className="flex items-center gap-1.5">
              <span className="shrink-0 rounded bg-neutral-100 px-1 text-neutral-500">
                {type}
              </span>
              <span className="shrink-0 text-neutral-400">{b.range}</span>
              <span className="truncate text-neutral-700">{b.tagKey}</span>
            </div>
            {b.reason && <p className="pl-1 text-neutral-400">{b.reason}</p>}
          </div>
        ))}
      </div>
    );
  }

  if (call.kind === "warning") {
    const member = call.request.memberKey as string | undefined;
    const requested = call.request.requestedAxis as string | undefined;
    const fallback = call.response.fallbackAxis as string | undefined;
    const skipped = call.response.skipped === true;
    return (
      <div className="flex flex-col gap-1 text-[11px]">
        <div className="flex items-center gap-1.5">
          <span className="shrink-0 rounded bg-neutral-100 px-1 text-neutral-500">
            멤버
          </span>
          <span className="truncate text-neutral-700">{member ?? "—"}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="shrink-0 rounded bg-neutral-100 px-1 text-neutral-500">
            요청 축
          </span>
          <span className="truncate text-neutral-700">{requested ?? "—"}</span>
        </div>
        {skipped ? (
          <p className="text-amber-600">
            도메인이 있는 축이 없어 이 멤버를 위계에서 건너뛰었습니다.
          </p>
        ) : (
          <div className="flex items-center gap-1.5">
            <span className="shrink-0 rounded bg-amber-100 px-1 text-amber-700">
              대체 축
            </span>
            <span className="truncate text-neutral-700">{fallback ?? "—"}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div>
        <p className="text-[11px] font-medium text-neutral-400">요청</p>
        <JsonBlock value={call.request} />
      </div>
      <div>
        <p className="text-[11px] font-medium text-neutral-400">응답</p>
        <JsonBlock value={call.response} />
      </div>
    </>
  );
};

/** trace 이벤트 1건(도구 호출/참조/최종 응답/대화). 클릭하면 상세를 토글로 펼친다. */
export const AutoTagToolCallItem = ({ call }: { call: AutoTagToolCall }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-neutral-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left"
      >
        <ChevronRight
          className={cn(
            "size-3 shrink-0 text-neutral-400 transition-transform",
            open && "rotate-90",
          )}
        />
        <span className="shrink-0 text-[11px] text-neutral-400">
          #{call.seq}
        </span>
        <span className="truncate text-xs text-neutral-700">
          {headerLabel(call)}
        </span>
        {call.kind === "warning" ? (
          <span className="shrink-0 text-[11px] text-amber-600">
            {call.ok ? "대체" : "건너뜀"}
          </span>
        ) : (
          !call.ok && (
            <span className="shrink-0 text-[11px] text-red-500">실패</span>
          )
        )}
        {call.durationMs > 0 && (
          <span className="ml-auto shrink-0 text-[11px] text-neutral-300">
            {call.durationMs}ms
          </span>
        )}
      </button>
      {open && (
        <div className="flex flex-col gap-1 px-2 pb-2">
          <Body call={call} />
        </div>
      )}
    </div>
  );
};
